import React, { useMemo, useState } from "react";
import { Modal } from "react-bootstrap";

import { btnGhost, btnPrimary, fmtDate } from "./config";

// Shown when an upload carries customer ids that already exist under a
// different partner code. The user picks, for every customer, which partner it
// actually belongs to; nothing is uploaded until all of them are answered.
// `onConfirm` receives { "<uploadingPartner>|<userId>": "<actual partner>" }.
export const resolutionKey = (c) => `${c.uploadingPartner}|${c.userId}`;

const DuplicateCustomersModal = ({ conflicts, onConfirm, onCancel }) => {
  const [choices, setChoices] = useState({});
  const list = useMemo(() => conflicts || [], [conflicts]);

  const partnerChoices = useMemo(
    () => [
      ...new Set(
        list.flatMap((c) => [c.uploadingPartner, ...c.existing.map((e) => e.partnerCode)]),
      ),
    ],
    [list],
  );
  const answered = list.filter((c) => choices[resolutionKey(c)]).length;

  // "Everything belongs to X" — only fills the customers X is valid for.
  const applyAll = (partnerCode) =>
    setChoices((prev) => {
      const next = { ...prev };
      list.forEach((c) => {
        if (c.uploadingPartner === partnerCode || c.existing.some((e) => e.partnerCode === partnerCode)) {
          next[resolutionKey(c)] = partnerCode;
        }
      });
      return next;
    });

  return (
    <Modal show={!!conflicts} onHide={onCancel} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Same customer id under two partners</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-sm text-gray-600">
          {list.length} customer id(s) in this upload already exist under another
          partner code. Pick the partner each customer really belongs to.
          Choosing the <strong>other</strong> partner leaves the customer out of
          this upload. Choosing the <strong>uploading</strong> partner imports
          them and removes the other copy (kept if it has a modem case, follow-up
          work or notes).
        </p>
        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
          <span className="font-semibold text-gray-500">Apply to all:</span>
          {partnerChoices.map((p) => (
            <button key={p} type="button" className={btnGhost} onClick={() => applyAll(p)}>
              All belong to {p}
            </button>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                <th className="px-3 py-2 text-left">Customer</th>
                <th className="px-3 py-2 text-left">Id</th>
                <th className="px-3 py-2 text-left">Which partner is correct?</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {list.map((c) => {
                const key = resolutionKey(c);
                return (
                  <tr key={key + (c.worksheetName || "")} className="align-top">
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-800">{c.name || "—"}</div>
                      <div className="text-xs text-gray-400">{c.phone || ""}</div>
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {c.userId}
                      {c.fileName && (
                        <div className="text-[11px] text-gray-400">
                          {c.fileName}
                          {c.worksheetName ? ` · ${c.worksheetName}` : ""}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <label className="mb-1 flex cursor-pointer items-start gap-2">
                        <input
                          type="radio"
                          name={key}
                          checked={choices[key] === c.uploadingPartner}
                          onChange={() => setChoices((p) => ({ ...p, [key]: c.uploadingPartner }))}
                        />
                        <span>
                          <strong>{c.uploadingPartner}</strong>{" "}
                          <span className="text-xs text-gray-400">
                            (this upload{c.sheetStatus ? ` — ${c.sheetStatus}` : ""})
                          </span>
                        </span>
                      </label>
                      {c.existing.map((e) => (
                        <label key={e.partnerCode} className="mb-1 flex cursor-pointer items-start gap-2">
                          <input
                            type="radio"
                            name={key}
                            checked={choices[key] === e.partnerCode}
                            onChange={() => setChoices((p) => ({ ...p, [key]: e.partnerCode }))}
                          />
                          <span>
                            <strong>{e.partnerCode}</strong>{" "}
                            <span className="text-xs text-gray-400">
                              (already in system — {e.status}
                              {e.onSheet ? ", on its latest sheet" : ", not on its latest sheet"}
                              {e.lastSeenDate ? `, last seen ${fmtDate(e.lastSeenDate)}` : ""}
                              {e.hasCase ? ", has modem case" : ""}
                              {e.hasFollowup ? ", has follow-up" : ""})
                            </span>
                          </span>
                        </label>
                      ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <span className="mr-auto text-xs text-gray-500">
          {answered} of {list.length} answered
        </span>
        <button type="button" className={btnGhost} onClick={onCancel}>
          Cancel upload
        </button>
        <button
          type="button"
          className={btnPrimary}
          disabled={answered < list.length}
          onClick={() => onConfirm(choices)}
        >
          Continue upload
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default DuplicateCustomersModal;
